import {
  Alert,
  AlertIcon,
  Box,
  Card,
  CardBody,
  Checkbox,
  Divider,
  Flex,
  Heading,
  Link,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  EspdResponseDataType,
  ParticipationRequestStatus,
  useSubmitParticipationRequestMutation,
  type ParticipationRequestDto,
  type ParticipationRequestPatchDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { StepNavigation } from "../../../../common/StepNavigation";
import { useApiError } from "../../../../common/errors/useApiError";
import { useEditor } from "../../../../common/editing/save-coordinator";
import { composeCriterionKey as criterionKey } from "../../../../common/qualification/criterionKey";
import { emptyEspd, espdAnswerFor } from "../bid/espd/useEspdAnswers";
import { usePortalEventUrls } from "../usePortalEventUrls";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";

interface Props {
  eventId: string;
  requestId: string;
  // Query-fresh request: the submitted state must reflect the refetch after the submit
  // mutation, which the editor coordinator's captured state never does.
  request: ParticipationRequestDto;
  event: PortalSourcingEventDetailDto;
  previousStep?: () => void;
}

export const RequestReviewStep = ({ eventId, requestId, request, event, previousStep }: Props) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const apiError = useApiError();
  const portalUrls = usePortalEventUrls();
  const { state, updateField, flushPending, fieldStatus } = useEditor<
    ParticipationRequestDto,
    ParticipationRequestPatchDto
  >(requestId);
  const [submitRequest, { isLoading: isSubmitting }] = useSubmitParticipationRequestMutation();

  const espd = state.espdResponse ?? emptyEspd();
  const finalDeclarationConfirmed = espd.finalDeclaration?.confirmed ?? false;
  const isSavingDeclaration =
    fieldStatus("espdResponse") === "saving" || fieldStatus("espdResponse") === "pending";

  const exclusionCriteria = event.applicableExclusionCriteria ?? [];
  const exclusionAnswered = exclusionCriteria.filter((criterion) => {
    const lead = criterion.requirementGroups[0]?.requirements.find(
      (r) => r.responseDataType === EspdResponseDataType.Indicator,
    );
    return lead ? espdAnswerFor(espd, lead.id)?.indicator !== undefined : true;
  }).length;
  const qualificationCriteria = event.selectionCriteria ?? [];
  const qualificationAnswered = qualificationCriteria.filter((criterion) => {
    const response = (state.qualificationResponses ?? []).find(
      (r) => r.criterionKey === criterionKey(criterion),
    );
    return (response?.description ?? "").trim().length > 0;
  }).length;

  const setFinalDeclaration = (confirmed: boolean) =>
    updateField({ espdResponse: { ...espd, finalDeclaration: { confirmed } } }, { debounceMs: 300 });

  const submit = async () => {
    try {
      await flushPending();
      await submitRequest({ requestId }).unwrap();
      window.scrollTo(0, 0);
    } catch (e) {
      apiError.trigger(e);
    }
  };

  if (request.status !== ParticipationRequestStatus.Draft) {
    return (
      <Box pb="8" pt="6">
        <Alert status="success" rounded="xl" alignItems="flex-start">
          <AlertIcon />
          <Box>
            <Text fontWeight="semibold">{t("Your request to participate has been submitted")}</Text>
            <Text fontSize="sm" color="smMuted">
              {request.submittedAt
                ? `${t("Submitted")} ${displayDateWithTime(request.submittedAt)}. `
                : null}
              {t(
                "The buyer reviews all requests after the deadline. Qualified suppliers get the invitation to tender with the bid deadline.",
              )}
            </Text>
            <Link
              as={RouterLink}
              to={portalUrls.view.go(eventId)}
              color="smPrimary"
              fontSize="sm"
              fontWeight="semibold"
              display="inline-block"
              mt="2"
            >
              {t("Back to the procurement")}
            </Link>
          </Box>
        </Alert>
      </Box>
    );
  }

  // Mirrors the server-side completeness gate: submission is one-shot, so an incomplete
  // request must never reach the BFF only to bounce with a field-less 400.
  const readyToSubmit =
    finalDeclarationConfirmed &&
    exclusionAnswered === exclusionCriteria.length &&
    qualificationAnswered === qualificationCriteria.length;

  return (
    <Box pb="8" pt="6">
      <Heading size="md" mb="2">
        {t("Summary and submission")}
      </Heading>
      <Text fontSize="sm" color="smMuted" mb="6">
        {t("Review the request before submitting. It can not be changed after submission.")}
      </Text>

      <Card variant="outline" rounded="xl" mb="6">
        <CardBody>
          <Stack spacing="3">
            {exclusionCriteria.length > 0 && (
              <SummaryRow
                label={t("Grounds for exclusion")}
                value={t("{{answered}} of {{total}} answered", {
                  answered: exclusionAnswered,
                  total: exclusionCriteria.length,
                })}
                complete={exclusionAnswered === exclusionCriteria.length}
              />
            )}
            {exclusionCriteria.length > 0 && qualificationCriteria.length > 0 && (
              <Divider borderColor="smBorder" />
            )}
            {qualificationCriteria.length > 0 && (
              <SummaryRow
                label={t("Qualification requirements")}
                value={t("{{answered}} of {{total}} answered", {
                  answered: qualificationAnswered,
                  total: qualificationCriteria.length,
                })}
                complete={qualificationAnswered === qualificationCriteria.length}
              />
            )}
          </Stack>
        </CardBody>
      </Card>

      <Box
        mb="6"
        borderWidth="1px"
        borderRadius="md"
        p="3"
        borderColor={finalDeclarationConfirmed ? "smSuccessSoftBorder" : "smBorder"}
        bg={finalDeclarationConfirmed ? "smSuccessSoft" : "smBackground"}
      >
        <Flex align="center" gap="3">
          <Checkbox
            colorScheme="teal"
            isChecked={finalDeclarationConfirmed}
            onChange={(e) => setFinalDeclaration(e.target.checked)}
            sx={{ ".chakra-checkbox__label": { opacity: 1 } }}
          >
            <Text fontSize="sm">
              {t(
                "I declare that the information in this self-declaration and the answers above are correct and complete.",
              )}
            </Text>
          </Checkbox>
          {isSavingDeclaration && <Spinner size="sm" color="smMuted" />}
        </Flex>
      </Box>

      <StepNavigation
        onPrevious={previousStep}
        onNext={submit}
        nextLabel={t("Submit request to participate")}
        nextIsAction
        isNextLoading={isSubmitting}
        isNextDisabled={!readyToSubmit}
      />
    </Box>
  );
};

const SummaryRow = ({ label, value, complete }: { label: string; value: string; complete: boolean }) => (
  <Flex justify="space-between" align="center" gap="3" flexWrap="wrap">
    <Text fontSize="sm" fontWeight="semibold">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="semibold" color={complete ? "smSuccess" : "smWarning"}>
      {value}
    </Text>
  </Flex>
);
